// AST Graph types and Zod schemas (ARCHITECTURE.md §4.4).
//
// The AST Graph is built from `FileAnalysisResult[]` and is fully
// language-agnostic. Once a file has been analyzed and merged into the graph,
// language identity is erased — `AstNode` deliberately carries NO `language`
// field. Downstream stages (dependency, impact, context) consume this graph
// without ever branching on the original source language.
//
// As elsewhere in the codebase, the inferred Zod types are the compile-time
// source of truth; the schemas provide runtime validation at trust boundaries
// (cache deserialization, plugin input).

import { z } from "zod";

import { GraphEdgeSchema, GraphNodeSchema } from "../types.js";
import { SymbolInfoSchema } from "../analyzer/types.js";

// ---------------------------------------------------------------------------
// AstNode
// ---------------------------------------------------------------------------

// AstNode extends GraphNode (§4.1) but narrows `kind` to the two structural
// node kinds the AST graph produces, and adds source-location fields.
export const AstNodeSchema = GraphNodeSchema.extend({
  kind: z.enum(["file", "symbol"]),
  filePath: z.string(),
  // Present on `file` nodes: every symbol the file declares.
  symbols: z.array(SymbolInfoSchema).optional(),
  // Present on `symbol` nodes: the symbol this node represents.
  symbolInfo: SymbolInfoSchema.optional(),
});

export type AstNode = z.infer<typeof AstNodeSchema>;

// ---------------------------------------------------------------------------
// AstGraph (in-memory)
// ---------------------------------------------------------------------------

// The canonical in-memory representation uses native Maps, matching the
// interface in §4.4. `z.map()` validates the live Map instances; this is the
// schema to use when checking an AstGraph that already exists in memory.
export const AstGraphSchema = z.object({
  nodes: z.map(z.string(), AstNodeSchema),
  edges: z.array(GraphEdgeSchema),
  // path → SHA-256
  fileHashes: z.map(z.string(), z.string()),
});

export type AstGraph = z.infer<typeof AstGraphSchema>;

// ---------------------------------------------------------------------------
// SerializedAstGraph (JSON / cache form)
// ---------------------------------------------------------------------------

// Maps do not survive `JSON.stringify`, so the persistent cache and any
// plugin/CLI boundary that crosses JSON use a record-based representation.
// `serializeAstGraph` / `deserializeAstGraph` convert between the two forms.
export const SerializedAstGraphSchema = z.object({
  nodes: z.record(z.string(), AstNodeSchema),
  edges: z.array(GraphEdgeSchema),
  fileHashes: z.record(z.string(), z.string()),
});

export type SerializedAstGraph = z.infer<typeof SerializedAstGraphSchema>;

export function serializeAstGraph(graph: AstGraph): SerializedAstGraph {
  return {
    nodes: Object.fromEntries(graph.nodes),
    edges: graph.edges,
    fileHashes: Object.fromEntries(graph.fileHashes),
  };
}

export function deserializeAstGraph(serialized: SerializedAstGraph): AstGraph {
  return {
    nodes: new Map(Object.entries(serialized.nodes)),
    edges: serialized.edges,
    fileHashes: new Map(Object.entries(serialized.fileHashes)),
  };
}
